import { trpc } from '@/lib/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { PromptTemplateForm, type PromptTemplateFormData } from './add';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { isPromptTemplateIcon, isPromptTemplateIconColor } from '@astro/shared-ui';
import type { PromptTemplateAudience } from '@astro/shared-ui';
import { audienceToFormValues, formValuesToAudience } from '@/features/promptTemplates/audience';

export const Route = createFileRoute('/_authenticated/prompt-templates/$id')({
  component: EditPromptTemplate,
  beforeLoad: () => {
    return {
      pageTitle: 'Edit Prompt Template',
    };
  },
});

function EditPromptTemplate() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  // Typed as strings, not form options: a stored value outside the allow-list is dropped below
  // rather than loaded into the Select, where it would fail validation on every later save.
  const { data: promptTemplate, isLoading } = useQuery(
    trpc.promptTemplate.findOne.queryOptions({ id })
  ) as {
    data?: Omit<
      PromptTemplateFormData,
      'icon' | 'iconColor' | 'audienceMode' | 'audienceSystems'
    > & {
      icon?: string | null;
      iconColor?: string | null;
      audience?: PromptTemplateAudience;
      hasInvalidAudience: boolean;
    };
    isLoading: boolean;
  };

  const { data: agentsData } = useQuery(trpc.agent.list.queryOptions({ page: 1, limit: 100 }));

  const { mutate } = useMutation(trpc.promptTemplate.updateOne.mutationOptions());

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PromptTemplateFormData>({
    defaultValues: { audienceMode: 'everyone', audienceSystems: [] },
    values: promptTemplate
      ? {
          name: promptTemplate.name,
          prompt: promptTemplate.prompt,
          agentId: promptTemplate.agentId,
          description: promptTemplate.description ?? '',
          icon:
            promptTemplate.icon && isPromptTemplateIcon(promptTemplate.icon)
              ? promptTemplate.icon
              : '',
          iconColor:
            promptTemplate.iconColor && isPromptTemplateIconColor(promptTemplate.iconColor)
              ? promptTemplate.iconColor
              : '',
          isPublished: promptTemplate.isPublished ?? false,
          autoResponse: promptTemplate.autoResponse ?? '',
          order: promptTemplate.order ?? 0,
          ...audienceToFormValues(promptTemplate.audience),
        }
      : undefined,
  });

  const onSubmit = (data: PromptTemplateFormData) => {
    if (!data.agentId) return;
    mutate(
      {
        id,
        data: {
          name: data.name,
          prompt: data.prompt.trim(),
          agentId: data.agentId,
          description: data.description?.trim() || null,
          icon: data.icon || null,
          iconColor: data.iconColor || null,
          isPublished: data.isPublished ?? false,
          autoResponse: data.autoResponse?.trim() || null,
          order: data.order ?? 0,
          audience: formValuesToAudience(data),
        },
      },
      {
        onSuccess: () => {
          navigate({ to: '/prompt-templates' });
        },
      }
    );
  };

  const onCancel = () => {
    navigate({ to: '/prompt-templates' });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <PromptTemplateForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      control={control}
      watch={watch}
      setValue={setValue}
      title="Edit Prompt Template"
      agents={agentsData?.agents || []}
      hasInvalidAudience={promptTemplate?.hasInvalidAudience ?? false}
    />
  );
}
